import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Radio, Select, Space, Table, Tag, message } from 'antd';
import { NotificationOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { cleanI18n } from '../../components/I18nFieldTabs';

const LEVEL = { critical: 'red', warning: 'orange', info: 'blue' };
const CATEGORIES = ['product_news', 'billing_info', 'usage', 'cdn', 'dns', 'tm_delivery', 'service_down', 'security', 'billing_critical'];

/**
 * Admin notices: one message to all (or chosen) users, with a title/body per language.
 * Each user gets it in their console language (inbox, email, Telegram per the category's channel rules).
 */
export default function AdminNotices() {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const audience = Form.useWatch('audience', form);
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiClient.get('/api/admin/notifications/broadcasts');
            setRows(r.data?.data?.items || []);
        } catch (e) {
            message.error(e.response?.data?.error || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const send = async () => {
        const v = await form.validateFields();
        setSending(true);
        try {
            const r = await apiClient.post('/api/admin/notifications/broadcast', {
                ...v,
                user_ids: (v.user_ids || []).map(Number).filter(Boolean),
                i18n: cleanI18n(v.i18n),
            });
            const d = r.data?.data || {};
            message.success(t('Sent to {{sent}} / {{total}} users', { sent: d.sent ?? 0, total: d.total ?? 0 }));
            setOpen(false);
            form.resetFields();
            load();
        } catch (e) {
            message.error(e.response?.data?.error || t('Send failed'));
        } finally {
            setSending(false);
        }
    };

    const columns = [
        { title: t('Time'), dataIndex: 'created_at', width: 160 },
        { title: t('Title'), dataIndex: 'title', ellipsis: true },
        { title: t('Category'), dataIndex: 'category', width: 130, render: (v, r) => <Tag color={LEVEL[r.level]}>{v}</Tag> },
        { title: t('Recipients'), dataIndex: 'recipients', width: 90 },
        { title: t('Read'), dataIndex: 'read', width: 80 },
    ];

    return (
        <Card size="small" title={t('Announcements')}
            extra={<Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                <Button size="small" type="primary" icon={<NotificationOutlined />} onClick={() => setOpen(true)}>{t('New announcement')}</Button>
            </Space>}>
            <Table rowKey="batch" size="small" loading={loading} columns={columns} dataSource={rows} pagination={false} />

            <Modal open={open} title={t('New announcement')} width={640} onCancel={() => setOpen(false)}
                footer={[
                    <Button key="c" onClick={() => setOpen(false)}>{t('Cancel')}</Button>,
                    <Popconfirm key="s" title={t('Send this announcement now?')} onConfirm={send}>
                        <Button type="primary" loading={sending}>{t('Send')}</Button>
                    </Popconfirm>,
                ]}>
                <Form form={form} layout="vertical" initialValues={{ audience: 'all', category: 'product_news', level: 'info' }}>
                    <Space wrap size={16}>
                        <Form.Item name="audience" label={t('Recipients')}>
                            <Radio.Group optionType="button" options={[{ value: 'all', label: t('All users') }, { value: 'users', label: t('Specific users') }]} />
                        </Form.Item>
                        <Form.Item name="category" label={t('Category')} tooltip={t('Delivery channels follow the category rules (important categories go to every channel)')}>
                            <Select style={{ width: 160 }} options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
                        </Form.Item>
                        <Form.Item name="level" label={t('Level')}>
                            <Select style={{ width: 110 }} options={['info', 'warning', 'critical'].map((c) => ({ value: c, label: c }))} />
                        </Form.Item>
                    </Space>
                    {audience === 'users' && (
                        <Form.Item name="user_ids" label={t('User IDs')} rules={[{ required: true }]}>
                            <Select mode="tags" tokenSeparators={[',', ' ']} open={false} />
                        </Form.Item>
                    )}
                    <I18nFieldTabs
                        fields={[{ key: 'title', label: t('Title') }, { key: 'body', label: t('Content'), type: 'textarea', rows: 5 }]}
                        base={<>
                            <Form.Item name="title" label={t('Title')} rules={[{ required: true, whitespace: true, max: 255 }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="body" label={t('Content')} rules={[{ max: 8000 }]}>
                                <Input.TextArea rows={5} />
                            </Form.Item>
                        </>}
                    />
                    <Form.Item name="link" label={t('Link')} tooltip={t('Console path such as /billing, or an https URL')}>
                        <Input placeholder="/billing" />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
}
